import { GetProgrammaticCampaignTopAccountsRO } from "@intentsify/dto";
import { getExportFileName, isPopulatedArray } from "@intentsify/utils";
import { useQuery } from "@tanstack/react-query";
import { DataToCsvDownload, ViewContainer } from "components";
import { useMemo } from "react";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { DisplayPerformanceFilters } from "../../../../types";
import { useDataForExport } from "./TopAccounts.utils";
import { TopAccountsTable } from "./TopAccountsTable";

const TITLE = "Top 25 Accounts";

type TopAccountsProps = {
  campaignName: string;
  campaignId: string | number;
  filters: DisplayPerformanceFilters;
  getTopAccounts: (
    id: string | number,
    filters: DisplayPerformanceFilters,
  ) => Promise<GetProgrammaticCampaignTopAccountsRO>;
  hideSpendColumn?: boolean;
};

const TopAccounts = (props: TopAccountsProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <TopAccountsContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const TopAccountsContent = ({
  campaignName,
  campaignId,
  filters,
  getTopAccounts,
  hideSpendColumn,
}: TopAccountsProps) => {
  const { data, isFetching: isLoading } = useQuery(
    ["getTopAccounts", campaignId, filters],
    () => getTopAccounts(campaignId, filters),
  );

  const hasData = !isLoading && isPopulatedArray(data?.topAccounts);

  const { hasVisits, hasConversions } =
    useMemo(() => {
      if (!isPopulatedArray(data?.topAccounts)) {
        return {
          hasVisits: false,
          hasConversions: false,
        };
      }

      return {
        hasVisits:
          data?.topAccounts.some((i) => Number(i.totalSiteVisits) > 0) ?? false,
        hasConversions:
          data?.topAccounts.some((i) => Number(i.conversions) > 0) ?? false,
      };
    }, [data?.topAccounts]) ?? false;

  const exportFileName = getExportFileName({
    prefix: `top_25_accounts_${campaignName}`,
    data: { id: campaignId },
    extension: ".csv",
  });

  const dataForExport = useDataForExport(
    data?.topAccounts ?? [],
    hasVisits,
    hasConversions,
    hideSpendColumn,
  );

  return (
    <ViewContainer
      withBackground
      title={TITLE}
      actionItems={
        hasData ? (
          <DataToCsvDownload
            fileName={exportFileName}
            data={dataForExport}
            downloadLabel={TITLE}
            iconSize={"xs"}
            withLabel
            subject="account"
            subjectPlural="accounts"
            tooltipPlacement="bottom-end"
          />
        ) : undefined
      }
    >
      <TopAccountsTable
        data={data}
        isLoading={isLoading}
        hasVisits={hasVisits}
        hasConversions={hasConversions}
        hideSpendColumn={hideSpendColumn}
      />
    </ViewContainer>
  );
};

export { TopAccounts };
